/**
 * Anchor inference for click-correct mode (5-C, extended by D1 2026-09-30).
 *
 * Given a value bbox the user just selected on the document and the full set
 * of OCR words on the same page, return the label PHRASE that precedes the
 * value, the union bbox of that phrase's tokens, and the relative
 * `value_position`.
 *
 * Search order:
 *   1. Same-row leftward → the closest label-stop token strictly to the left
 *      of the value on the same row (closest = max x2); value_position =
 *      'right_of_anchor'.
 *   2. Above (fallback) → the closest label-stop token strictly above the
 *      value whose x-range overlaps the value's (closest = max y2);
 *      value_position = 'below_anchor'.
 *   3. Otherwise → anchorText=null, value_position='region_only'.
 *
 * WHY A PHRASE (D1). OCR tokens are single words, so the label-stop token of
 * "Invoice Number:" is just "Number:". A pattern anchored on "Number:" matches
 * the FIRST "Number:" in reading order on the next document — "PO Number:",
 * "Account Number:" — and extracts the wrong field. From the label-stop token
 * the phrase walk goes LEFT along that token's row, joining tokens until a
 * horizontal gap wider than twice the phrase's average character width, or
 * another label-stop token (the end of the PREVIOUS label on the same line).
 * vault: notes/capture/2026-09-26-review-time-profile-field-editing.md, D1.
 *
 * The label-stop and same-row rules mirror the backend module
 * capture-app/src/services/extraction/anchorMatching.ts. Keep them in sync;
 * the boundary policy (REPO_BOUNDARIES.md) requires we duplicate rather
 * than import across services.
 */

/** Mirror of backend isSameRow — bidirectional center-in-range check. */
export function isSameRow(a, b) {
  const aCY = (a.y1 + a.y2) / 2;
  const bCY = (b.y1 + b.y2) / 2;
  return (aCY >= b.y1 && aCY <= b.y2) || (bCY >= a.y1 && bCY <= a.y2);
}

/** Mirror of backend isLabelStop. Token ends a label phrase. */
export function isLabelStop(text) {
  if (!text) return false;
  const trimmed = String(text).trim();
  if (!trimmed) return false;
  // Rule 2: pure separator tokens (|, broken bar, bullets, box-drawing chars)
  if (/^[|¦•│┃▀-▟]+$/.test(trimmed)) return true;
  // Rule 1: token ends in ':' with at least one alnum char before it
  if (/[A-Za-z0-9].*:$/.test(trimmed)) return true;
  return false;
}

/** True iff [a1, a2] and [b1, b2] overlap on the same axis. */
function rangesOverlap(a1, a2, b1, b2) {
  return a1 <= b2 && b1 <= a2;
}

/**
 * Union bbox of an array of OCR words (each with `bbox: {x1,y1,x2,y2}`).
 * Returns null if `words` is empty/invalid.
 */
export function computeUnionBbox(words) {
  if (!Array.isArray(words) || words.length === 0) return null;
  let x1 = Infinity, y1 = Infinity, x2 = -Infinity, y2 = -Infinity;
  for (const w of words) {
    if (!w?.bbox) continue;
    if (w.bbox.x1 < x1) x1 = w.bbox.x1;
    if (w.bbox.y1 < y1) y1 = w.bbox.y1;
    if (w.bbox.x2 > x2) x2 = w.bbox.x2;
    if (w.bbox.y2 > y2) y2 = w.bbox.y2;
  }
  if (!Number.isFinite(x1)) return null;
  return { x1, y1, x2, y2 };
}

/**
 * Walk left from a label-stop token along its row, joining the words that
 * belong to the same label phrase.
 *
 * Stops at (a) a horizontal gap wider than PHRASE_GAP_CHARS average character
 * widths of the phrase collected so far, or (b) another label-stop token —
 * that token closes the previous label on the line ("Date: 01/02 PO Number:"
 * must yield "PO Number:", not "Date: 01/02 PO Number:"). Value words are
 * never part of the phrase (excludeIds).
 *
 * @returns {{text: string, bbox: {x1,y1,x2,y2}}}
 */
export const PHRASE_GAP_CHARS = 2;

export function collectLabelPhrase(stopWord, candidates) {
  const phrase = [stopWord];
  const rowPeers = candidates.filter(
    (w) => w !== stopWord && w.bbox.x2 <= stopWord.bbox.x1 + 0.005 && isSameRow(w.bbox, stopWord.bbox),
  );
  for (;;) {
    const leftmost = phrase[0];
    const chars = phrase.reduce((n, w) => n + String(w.text).length, 0);
    const width = phrase.reduce((n, w) => n + (w.bbox.x2 - w.bbox.x1), 0);
    const avgCharWidth = chars > 0 ? width / chars : 0;
    const maxGap = avgCharWidth * PHRASE_GAP_CHARS;

    const next = rowPeers
      .filter((w) => !phrase.includes(w) && w.bbox.x2 <= leftmost.bbox.x1 + 0.005)
      .sort((a, b) => b.bbox.x2 - a.bbox.x2)[0];
    if (!next) break;
    if (leftmost.bbox.x1 - next.bbox.x2 > maxGap) break;
    if (isLabelStop(next.text)) break;
    phrase.unshift(next);
  }
  return {
    text: phrase.map((w) => String(w.text).trim()).join(' '),
    bbox: computeUnionBbox(phrase),
  };
}

/**
 * One OCR token that prints a label AND its value: "Phone: (800) 800-4239".
 *
 * PaddleOCR emits a printed phrase as a single token on most scanned rows, so
 * a click on such a row selects label and value together. Sending that whole
 * text as the value would be wrong, and editing it down by hand turns the
 * correction into a typed one that cannot teach. So the token is split here:
 * the part up to and including the first label colon is the anchor, the rest
 * is the value, and the position is 'inline_after_anchor' — the layout the
 * backend extractor already reads from a merged token
 * (profilePatternExtractor extractInlineFromAnchor). The approval-time replay
 * decides whether a pattern built from it is usable.
 *
 * Returns null when the text is not label-then-value: no colon, nothing after
 * it, no letter before it ("12:30 PM"), or a URL scheme ("http://...").
 *
 * @param {string} text
 * @returns {{label: string, value: string}|null}
 */
export function splitInlineLabel(text) {
  const trimmed = String(text ?? '').trim();
  const m = /^(.*?[A-Za-z][^:]*:)\s*(\S.*)$/.exec(trimmed);
  if (!m) return null;
  const label = m[1].trim();
  const value = m[2].trim();
  if (!value || value.startsWith('//')) return null;
  return { label, value };
}

/**
 * Run anchor inference against a value bbox and the page's OCR words.
 *
 * @param {{x1:number,y1:number,x2:number,y2:number}} valueBbox
 * @param {Array<{id:string,text:string,bbox:{x1:number,y1:number,x2:number,y2:number}}>} allWords
 *        — all OCR words on the same page as `valueBbox`.
 * @param {{excludeIds?: Set<string>}} [opts]
 *        — optional set of word IDs to exclude (e.g. the words that make up
 *        the value itself — they should never be considered as their own
 *        anchor).
 * @returns {{anchorText: string|null, anchorBbox: object|null,
 *            valuePosition: 'right_of_anchor'|'below_anchor'|'region_only'}}
 */
export function inferAnchor(valueBbox, allWords, opts = {}) {
  const none = { anchorText: null, anchorBbox: null, valuePosition: 'region_only' };
  if (!valueBbox || !Array.isArray(allWords) || allWords.length === 0) {
    return none;
  }
  const excludeIds = opts.excludeIds || new Set();

  const candidates = allWords.filter(
    w => w?.bbox && w.text && !excludeIds.has(w.id),
  );

  // 1. Same-row, strictly leftward.
  // Small tolerance on x2 ≤ value.x1 — OCR jitter can put a label's right
  // edge a hair past the value's left edge without the two actually
  // overlapping in any meaningful way.
  const sameRowLeft = candidates
    .filter(w => w.bbox.x2 <= valueBbox.x1 + 0.005)
    .filter(w => isSameRow(w.bbox, valueBbox))
    .filter(w => isLabelStop(w.text));

  if (sameRowLeft.length > 0) {
    sameRowLeft.sort((a, b) => b.bbox.x2 - a.bbox.x2); // closest = max x2
    const phrase = collectLabelPhrase(sameRowLeft[0], candidates);
    return { anchorText: phrase.text, anchorBbox: phrase.bbox, valuePosition: 'right_of_anchor' };
  }

  // 2. Above (fallback): strictly above + x-overlapping.
  const above = candidates
    .filter(w => w.bbox.y2 <= valueBbox.y1 + 0.005)
    .filter(w =>
      rangesOverlap(w.bbox.x1, w.bbox.x2, valueBbox.x1, valueBbox.x2),
    )
    .filter(w => isLabelStop(w.text));

  if (above.length > 0) {
    above.sort((a, b) => b.bbox.y2 - a.bbox.y2); // closest = max y2
    const phrase = collectLabelPhrase(above[0], candidates);
    return { anchorText: phrase.text, anchorBbox: phrase.bbox, valuePosition: 'below_anchor' };
  }

  // 3. No anchor found.
  return none;
}

/**
 * What OcrWordOverlay emits when a selection of OCR words is released: the
 * combined text, the union bbox, and the anchor for the value.
 *
 * Lifted out of the overlay's mouseup handler unchanged, so it can be tested
 * without rendering the overlay.
 *
 * `splitInlineLabels` (default false) is the one switch. When true, a
 * selection of ONE token that prints a label and its value together is split
 * (see splitInlineLabel): text is the value, the label is the anchor, there is
 * no anchor box, and the position is 'inline_after_anchor'. Only the Review
 * correction viewer turns it on. The overlay is shared, and
 * TemplateEditorPage's identifier pick stores `text` as the identifier itself,
 * so there "Vendor: Acme" must stay "Vendor: Acme".
 *
 * @param {Array<{id:string,text:string,bbox:{x1,y1,x2,y2}}>} ocrWords  every word on the page
 * @param {Set<string>} selectedWordIds
 * @param {{splitInlineLabels?: boolean}} [opts]
 * @returns {{id:string,text:string,bbox:object,anchorText:string|null,anchorBbox:object|null,valuePosition:string}|null}
 *          null when nothing with text is selected
 */
export function buildSelectionPayload(ocrWords, selectedWordIds, opts = {}) {
  const { splitInlineLabels = false } = opts;
  if (!Array.isArray(ocrWords) || !selectedWordIds || selectedWordIds.size === 0) return null;

  // Build combined text from selected words, sorted by position (top-to-bottom, left-to-right)
  const selectedWords = ocrWords.filter(w => selectedWordIds.has(w.id));

  // Sort by vertical position first (y1), then horizontal (x1)
  selectedWords.sort((a, b) => {
    const yDiff = a.bbox.y1 - b.bbox.y1;
    // If words are on roughly the same line (within 1% vertical tolerance), sort by x
    if (Math.abs(yDiff) < 0.01) {
      return a.bbox.x1 - b.bbox.x1;
    }
    return yDiff;
  });

  const combinedText = selectedWords.map(w => w.text).join(' ');
  if (!combinedText.trim()) return null;

  // 5-C: the union bbox of the selected words and the inferred anchor for the
  // value. The anchor search excludes the selected words themselves (they
  // should never be considered as their own anchor) and uses the full
  // page-level ocrWords list.
  const unionBbox = computeUnionBbox(selectedWords);
  // One token that prints label and value together ("Phone: (800) 800-4239",
  // the usual shape on a scan): the value is the part after the label, the
  // label is the anchor, and there is no separate anchor box to send.
  const inline = splitInlineLabels && selectedWords.length === 1 && unionBbox
    ? splitInlineLabel(combinedText)
    : null;
  const anchor = inline
    ? { anchorText: inline.label, anchorBbox: null, valuePosition: 'inline_after_anchor' }
    : unionBbox
      ? inferAnchor(unionBbox, ocrWords, { excludeIds: selectedWordIds })
      : { anchorText: null, anchorBbox: null, valuePosition: 'region_only' };

  return {
    id: 'multi-select',
    text: inline ? inline.value : combinedText.trim(),
    bbox: unionBbox,
    anchorText: anchor.anchorText,
    // D1: the label phrase's union bbox. Same page as the value — the
    // inference only ever searches this page's words.
    anchorBbox: anchor.anchorBbox,
    valuePosition: anchor.valuePosition,
  };
}
